import api from './api';

/**
 * Get paginated list of FAQs with filters
 * @param {Object} filters - Filter parameters
 * @param {number} page - Page number (default: 1)
 * @param {number} per_page - Items per page (default: 20)
 * @returns {Promise<Object>} Response with FAQs data and pagination info
 */
export const getFaqs = async (filters = {}, page = 1, per_page = 20) => {
  console.log('🚀 faqsService.getFaqs called with:', { filters, page, per_page });
  
  try {
    // Build query parameters
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString()
    });

    // Add filters to params
    if (filters.search) params.append('search', filters.search);
    if (filters.category) params.append('category', filters.category);
    if (filters.is_active !== undefined && filters.is_active !== '') params.append('is_active', filters.is_active.toString());
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const url = `/api/admin/faqs?${params.toString()}`;
    console.log('📡 FAQ API URL:', url);

    const response = await api.get(url);
    console.log('✅ FAQ API Response received:', {
      status: response.status,
      dataType: typeof response.data,
      isArray: Array.isArray(response.data),
      dataLength: Array.isArray(response.data) ? response.data.length : 'N/A',
      meta: response.data?.meta || 'No meta',
      success: response.data?.success
    });

    // Handle success_response with pagination from backend
    if (response.data?.success && response.data?.meta?.pagination) {
      // Paginated response format from success_response
      console.log('📄 Paginated FAQ response detected');
      return {
        data: response.data.data || [],
        pagination: response.data.meta.pagination,
        success: true
      };
    } else if (response.data?.success && response.data?.data) {
      // Success response without pagination
      console.log('📋 Success FAQ response detected');
      return {
        data: response.data.data,
        success: true
      };
    } else if (Array.isArray(response.data)) {
      // Direct array response (fallback)
      console.log('📋 Direct array FAQ response detected');
      return {
        data: response.data,
        success: true
      };
    } else {
      // Fallback for other formats
      console.log('📋 Using FAQ response as-is');
      return response.data || [];
    }

  } catch (error) {
    console.error('❌ getFaqs error:', error);
    throw error;
  }
};

/**
 * Get single FAQ by ID
 * @param {string} id - FAQ ID
 * @returns {Promise<Object>} FAQ data
 */
export const getFaqById = async (id) => {
  console.log('🔍 faqsService.getFaqById called with:', id);
  
  try {
    const response = await api.get(`/api/admin/faqs/${id}`);
    console.log('✅ FAQ detail response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ getFaqById error:', error);
    throw error;
  }
};

/**
 * Create new FAQ
 * @param {Object} faqData - FAQ data to create
 * @returns {Promise<Object>} Created FAQ data
 */
export const createFaq = async (faqData) => {
  console.log('➕ faqsService.createFaq called with:', faqData);
  
  try {
    const response = await api.post('/api/admin/faqs', faqData);
    console.log('✅ FAQ created:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ createFaq error:', error);
    throw error;
  }
};

/**
 * Update existing FAQ
 * @param {string} id - FAQ ID
 * @param {Object} faqData - FAQ data to update
 * @returns {Promise<Object>} Updated FAQ data
 */
export const updateFaq = async (id, faqData) => {
  console.log('📝 faqsService.updateFaq called with:', { id, faqData });
  
  try {
    const response = await api.put(`/api/admin/faqs/${id}`, faqData);
    console.log('✅ FAQ updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ updateFaq error:', error);
    throw error;
  }
};

/**
 * Delete FAQ
 * @param {string} id - FAQ ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteFaq = async (id) => {
  console.log('🗑️ faqsService.deleteFaq called with:', id);
  
  try {
    const response = await api.delete(`/api/admin/faqs/${id}`);
    console.log('✅ FAQ deleted:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ deleteFaq error:', error);
    throw error;
  }
};

/**
 * Get FAQ categories
 * @returns {Promise<Array>} List of FAQ categories
 */
export const getFaqCategories = async () => {
  console.log('📂 faqsService.getFaqCategories called');
  
  try {
    const response = await api.get('/api/admin/faqs/categories');
    console.log('✅ FAQ categories retrieved:', response.data);
    
    // Handle success_response format from backend
    if (response.data && response.data.success && response.data.data) {
      return response.data; // Return the full response for component to handle
    } else if (Array.isArray(response.data)) {
      // Direct array response (fallback)
      return response.data;
    } else {
      return response.data || [];
    }
  } catch (error) {
    console.error('❌ getFaqCategories error:', error);
    throw error;
  }
};

/**
 * Update FAQ display order in bulk
 * @param {Array} faqOrders - Array of {id, display_order} objects
 * @returns {Promise<Object>} Update confirmation
 */
export const updateFaqsOrder = async (faqOrders) => {
  console.log('🔄 faqsService.updateFaqsOrder called with:', faqOrders);
  
  try {
    const response = await api.put('/api/admin/faqs/bulk-order', { faq_orders: faqOrders });
    console.log('✅ FAQ order updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ updateFaqsOrder error:', error);
    throw error;
  }
};

/**
 * Update FAQ status in bulk
 * @param {Array} faqIds - Array of FAQ IDs
 * @param {boolean} isActive - New active status
 * @returns {Promise<Object>} Update confirmation
 */
export const updateFaqsStatus = async (faqIds, isActive) => {
  console.log('🔄 faqsService.updateFaqsStatus called with:', { faqIds, isActive });
  
  try {
    const response = await api.post('/api/admin/faqs/bulk-status', { 
      faq_ids: faqIds, 
      is_active: isActive 
    });
    console.log('✅ FAQ status updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ updateFaqsStatus error:', error);
    throw error;
  }
};

/**
 * Get FAQ statistics
 * @returns {Promise<Object>} FAQ statistics
 */
export const getFaqStats = async () => {
  console.log('📊 faqsService.getFaqStats called');
  
  try {
    const response = await api.get('/api/admin/faqs/stats/overview');
    console.log('✅ FAQ stats retrieved:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ getFaqStats error:', error);
    throw error;
  }
};